import {computed, observable, override} from 'mobx';
import {MatchEventsModuleStageViewModel} from "./match-events-module.stage-view-model";
import {MatchEventsModuleTabViewModel} from "./match-events-module.tab-view-model";
import {BaseModuleState} from "../../../../../infrastructure/models/base-module-state";
import {MATCH_EVENTS} from "../../../match-widget.consts";
import {MatchEventsModuleRowViewModel} from "./match-events-module.row-view-model";
import {EventDomainModel} from "../../../../../domain/common/events/models/event-domain-model";
import {EventViewModel} from "../../../../../domain/common/events/models/event-view-model";
import {StageDomainModel} from "../../../../../domain/full-game/models/stage/stage-domain-model";
import {GAME_STATUSES} from "../../../../../core/app/consts";
import {BaseMobxState} from "../../../../../infrastructure/mobx/base-mobx-state";
import {AppStore} from "../../../../../core/app/app-store";
import {LoggerUtils} from "../../../../../infrastructure/utils/logger/logger-utils";
import {DatetimeUtils} from "../../../../../infrastructure/utils/datetime-utils";
import {DeviceViewModel} from "../../../../../core/models/device.view-model";
import {TermsUtils} from "../../../../../infrastructure/utils/terms-utils";
import {NavigationManager} from "../../../../../core/managers/navigation-manager";
import {MatchEventsModuleConfig} from "../match-events.module-config";
import {MatchEventsModuleProps} from "./match-events-module.props";
import {FullGameDataSource} from "../../../../../domain/full-game/models/full-game-data-source";
import {LineupsMemberDomainModel} from "../../../../../domain/yard/lineups/lineups-member/lineups-member-domain-model";
import {LineupsMemberViewModel} from "../../../../../domain/yard/lineups/lineups-member/lineups-member-view-model";
import {COMPONENT_STATES} from "../../../../../core/consts";
import {LINEUPS} from "../../../../lineups-widget/lineups-widget.consts";

export class MatchEventsModuleState extends BaseModuleState {
  dataSource: FullGameDataSource;
  props: MatchEventsModuleProps;
  @observable selectedTabIndex: Number = undefined;
  @observable selectedAthleteId: Number;
  @observable isPlayerCardPopupVisible: Boolean;

  static injectables = [...BaseMobxState.injectables, NavigationManager, AppStore, LoggerUtils, DatetimeUtils, DeviceViewModel, TermsUtils, MatchEventsModuleConfig];

  @override get isPopulated() {
    let isPopulated = super.isPopulated;

    if (!isPopulated) {
      return false;
    }

    const hasStages = this.isMoreThanOneStage || this.isOnlyOneNotCurrentStage || this.hasEventsInCurrentStage || this.hasPlayByPlay;
    const hasEvents = this.events?.length > 0;

    isPopulated = this.props.alwaysShowStages ? hasStages : hasEvents;

    return isPopulated || this.componentState !== COMPONENT_STATES.loaded;
  }

  @computed get showAll() {
    const showAll = this.activeTab.index === MATCH_EVENTS.TABS.all.index;

    return showAll
  }

  @computed get activeTab() {
    const activeTab: MatchEventsModuleTabViewModel = this.tabs.find((tab: MatchEventsModuleTabViewModel) => tab.index === this.activeTabIndex);

    return activeTab;
  }

  @computed get activeTabIndex() {
    if (!isNaN(this.selectedTabIndex)) {
      return this.selectedTabIndex;
    }

    if (this.dataSource?.game.statusGroup === GAME_STATUSES.ended && this.showTopTab) {
      return MATCH_EVENTS.TABS.top.index
    }

    return MATCH_EVENTS.TABS.all.index;
  }

  @computed get showTabs(): Boolean {
    const showTabs: Boolean = this.tabs.length > 1;

    return showTabs;
  }

  @computed get showAllTab(): Boolean {
    const showAllTab: Boolean = this.hasPlayByPlay || this.hasEvents;

    return showAllTab;
  }

  @computed get showTopTab(): Boolean {
    const showTopTab: Boolean = this.hasMajorEvents && this.majorEvents.length !== this.events.length;

    return showTopTab;
  }

  @computed get events(): Array<EventViewModel> {
    const players: Array<LineupsMemberViewModel> = [this.dataSource?.game.homeCompetitor.lineups?.members, this.dataSource?.game.awayCompetitor.lineups?.members].flat();
    const managers: Array<Number> = players?.filter(player => player?.status === LINEUPS.MEMBERS_STATUS.management).map(staff => staff.id);

    const eventsVM: Array<EventViewModel> = this.dataSource?.game.events?.map((event: EventDomainModel) => {
      const isStaff: Boolean = managers.includes(event.player.id);

      return event ? new EventViewModel(event, this.terms.WEB_GAME_CENTER_FROM_THE_BENCH, isStaff) : null
    });
    const filteredEvents: Array<EventViewModel> = eventsVM?.filter((event: EventViewModel) => !!event?.gameTimeDisplay || !!event?.stageId);

    return filteredEvents;
  }

  @computed get hasEvents(): Boolean {
    const hasEvents: Boolean = this.events?.length > 0;

    return hasEvents;
  }

  @computed get majorEvents(): Array<EventViewModel> {
    const events: Array<EventViewModel> = this.events?.filter((event: EventViewModel) => event.isMajor);

    return events;
  }

  @computed get hasMajorEvents(): Boolean {
    const hasMajorEvents: Boolean = this.majorEvents?.length > 0;

    return hasMajorEvents;
  }

  @computed get visibleEvents(): Array<EventViewModel> {
    return this.activeTab?.isMajor ? this.majorEvents : this.events;
  }

  @computed get stages(): Array<MatchEventsModuleStageViewModel> {
    let stagesVM: Array<MatchEventsModuleStageViewModel> = this.dataSource?.game.stages?.map((stage: StageDomainModel) => {
      const scoreText = stage.homeCompetitorScore + ' - ' + stage.awayCompetitorScore;
      const eventRows: Array<MatchEventsModuleRowViewModel> = this.rows.filter((row: MatchEventsModuleRowViewModel) => stage.id === row.stageId);
      const isValidStage = (stage.homeCompetitorScore > -1 && stage.awayCompetitorScore > -1);

      if (!isValidStage) {
        return null;
      }

      return new MatchEventsModuleStageViewModel(stage, scoreText, eventRows);
    }).filter(x => !!x).reverse();

    let currentStageVM = stagesVM?.find((stage: MatchEventsModuleStageViewModel) => stage.isCurrent);

    if (currentStageVM) {
      this.rows?.forEach((row: MatchEventsModuleRowViewModel) => {
        const isExistingStage = stagesVM.find((stage: MatchEventsModuleStageViewModel) => row.stageId === stage.id);

        if (!isExistingStage) {
          currentStageVM.eventRows.push(row);
        }
      });
    }

    return stagesVM;
  }

  @computed get tabs(): Array<MatchEventsModuleTabViewModel> {
    const allTab: MatchEventsModuleTabViewModel = new MatchEventsModuleTabViewModel({
      index: MATCH_EVENTS.TABS.all.index,
      name: this.terms[MATCH_EVENTS.TABS.all.termKey],
      visible: true,
      active: this.activeTabIndex === MATCH_EVENTS.TABS.all.index,
      isMajor: MATCH_EVENTS.TABS.all.isMajor,
      type: MATCH_EVENTS.TABS.all.type
    });

    const topTab: MatchEventsModuleTabViewModel = new MatchEventsModuleTabViewModel({
      index: MATCH_EVENTS.TABS.top.index,
      name: this.terms[MATCH_EVENTS.TABS.top.termKey],
      visible: true,
      active: this.activeTabIndex === MATCH_EVENTS.TABS.top.index,
      isMajor: MATCH_EVENTS.TABS.top.isMajor,
      type: MATCH_EVENTS.TABS.top.type
    });

    const commentaryTab: MatchEventsModuleTabViewModel = new MatchEventsModuleTabViewModel({
      index: MATCH_EVENTS.TABS.commentary.index,
      name: this.terms[MATCH_EVENTS.TABS.commentary.termKey],
      visible: this.hasPlayByPlay,
      active: this.activeTabIndex === MATCH_EVENTS.TABS.commentary.index,
      type: MATCH_EVENTS.TABS.commentary.type
    });

    const tabs: Array<MatchEventsModuleTabViewModel> = [];

    this.showAllTab && tabs.push(allTab);
    this.showTopTab && tabs.push(topTab)
    this.hasPlayByPlay && tabs.push(commentaryTab)

    return tabs;
  }

  @computed get rows(): Array<MatchEventsModuleRowViewModel> {
    const rows: Array<MatchEventsModuleRowViewModel> = [];

    this.visibleEvents?.forEach((event: EventViewModel) => {
      const isHomeEvent: Boolean = event.competitorId === this.dataSource?.game.homeCompetitor.id;
      const matchingRows: Array<MatchEventsModuleRowViewModel> = rows.filter((row: MatchEventsModuleRowViewModel) => row.gameTime === event.gameTime && row.addedTime === event.addedTime);
      let existingRow = matchingRows.find((row: MatchEventsModuleRowViewModel) => isHomeEvent ? !row.homeEvent : !row.awayEvent);
      const shouldCreateNewRow: Boolean = !existingRow || (isHomeEvent ? existingRow.homeEvent : existingRow.awayEvent);

      if (shouldCreateNewRow) {
        existingRow = new MatchEventsModuleRowViewModel(isHomeEvent && event, !isHomeEvent && event, this.dataSource?.game.homeCompetitor.isNational);
        rows.push(existingRow);
      }

      if (isHomeEvent) {
        existingRow.homeEvent = event;
      } else {
        existingRow.awayEvent = event;
      }

    });

    return rows.sort((a: MatchEventsModuleRowViewModel, b: MatchEventsModuleRowViewModel) => b.order - a.order);
  }

  @computed get noEventLabel(): String {
    const noEventLabel: String = this.activeTabIndex === MATCH_EVENTS.TABS.top.index ? this.terms.WEB_GC_GAME_EVENTS_NO_TOP_EVENTS : this.terms.WEB_GC_GAME_EVENTS_NO_EVENTS

    return noEventLabel
  }

  @computed get isMoreThanOneStage(): Boolean {
    const isMoreThanOneStage: Boolean = this.stages?.length > 1

    return isMoreThanOneStage;
  }

  @computed get isOnlyOneStage(): Boolean {
    const isOnlyOneStage: Boolean = this.stages?.length === 1

    return isOnlyOneStage;
  }

  @computed get isOnlyOneNotCurrentStage(): Boolean {
    const isOnlyOneNotCurrentStage: Boolean = this.isOnlyOneStage && !this.stages?.first()?.isCurrent

    return isOnlyOneNotCurrentStage;
  }

  @computed get hasEventsInCurrentStage(): Boolean {
    const isCurrent: Boolean = this.stages?.first()?.isCurrent;
    const hasEventsInCurrentStage: Boolean = this.isOnlyOneStage && isCurrent && this.stages?.first()?.eventRows?.length > 0

    return hasEventsInCurrentStage;
  }

  @computed get hasPlayByPlay(): Boolean {
    const hasPlayByPlay: Boolean = !!this.dataSource?.game.playByPlay && this.appSettings.allowPremiumData;

    return hasPlayByPlay;
  }

  @computed get isCommentaryTab(): Boolean {
    const isCommentaryTab: Boolean = this.activeTabIndex === MATCH_EVENTS.TABS.commentary.index

    return isCommentaryTab;
  }

  @computed get shouldRenderPreviewPlayByPlay(): Boolean {
    const shouldRenderPreviewPlayByPlay: Boolean = this.hasPlayByPlay && !this.isCommentaryTab && this.gameStatus !== GAME_STATUSES.scheduled

    return shouldRenderPreviewPlayByPlay;
  }

  @computed get shouldRenderFooter(): Boolean {
    const shouldRenderFooter: Boolean = !this.isCommentaryTab

    return shouldRenderFooter;
  }

  @computed get isLive(): Boolean {
    return this.dataSource?.game.isLive;
  }

  get gameStatus(): GAME_STATUSES {
    return this.dataSource?.game.statusGroup;
  }

  @computed get shouldRenderStagesEmptyState() {
    return this.stages.length === 1 && this.stages.first().eventRows.length === 0;
  }

  @computed get hasAnyPlayerStat(): Boolean {
    const players: Array<LineupsMemberViewModel> = this.dataSource.game.homeCompetitor.lineups?.members
    const hasAnyPlayerStat: LineupsMemberDomainModel = players?.find((player: LineupsMemberDomainModel) => player.hasStats);

    return hasAnyPlayerStat !== undefined;
  }

  get isWidget(): Boolean {
    return this.appSettings.isWidget;
  }
}
